import { Link, useNavigate } from "@tanstack/react-router";
import {
  BarChart3,
  Bell,
  Calendar,
  ChevronDown,
  Globe,
  Lock,
  LogOut,
  Settings,
  type LucideIcon,
  User,
  UserPen,
  UserX,
} from "lucide-react";
import type React from "react";
import { type ComponentProps, useState } from "react";
import { useTranslation } from "react-i18next";
import { CustomAvatar } from "@/components/common/CustomAvatar";
import { AppToggle } from "@/components/navigation/AppToggle";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { BlockedUsersModal } from "@/features/user-setting/components/BlockedUsersModal";
import { EmailNotificationModal } from "@/features/user-setting/components/EmailNotificationModal";
import { LanguageModal } from "@/features/user-setting/components/LanguageModal";
import { LoginInfoModal } from "@/features/user-setting/components/LoginInfoModal";
import { ProfileEditModal } from "@/features/user-setting/components/ProfileEditModal";
import { UsageModal } from "@/features/user-setting/components/UsageModal";
import { useLogout } from "@/hooks/useLogout";
import { buildUserProfileFromPath } from "@/lib/navigation/userProfileNavigation";
import { getLanguageLabel } from "@/lib/utils/textUtils";
import { useGlobalUserInfo } from "@/providers/AuthProvider";

const MenuButton = ({
  IconComponent,
  ...props
}: { IconComponent: LucideIcon } & ComponentProps<typeof Button>) => {
  return (
    <Button
      variant="ghost"
      size="icon"
      className="size-9 rounded-full bg-white"
      {...props}
    >
      <IconComponent />
    </Button>
  );
};

type SettingsModalKey =
  | "profile"
  | "language"
  | "loginInfo"
  | "notification"
  | "blockedUsers"
  | "usage"
  | null;

export const Navbar: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { logout } = useLogout();
  const userInfo = useGlobalUserInfo();

  // Modal states
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isSettingsMenuOpen, setIsSettingsMenuOpen] = useState(false);
  const [activeModal, setActiveModal] = useState<SettingsModalKey>(null);

  // Handle logout
  const handleLogout = async () => {
    await logout();
  };

  // Navigate to user profile
  const navigateToUserProfile = () => {
    if (userInfo?.id) {
      const from = buildUserProfileFromPath(
        window.location.pathname,
        window.location.search,
        window.location.hash,
      );

      navigate({
        to: "/user-profile/$user-id",
        params: { "user-id": String(userInfo.id) },
        search: from ? { from } : {},
      });
    }
  };

  const openModal = (modal: Exclude<SettingsModalKey, null>) => {
    setActiveModal(modal);
    setIsMenuOpen(false);
    setIsSettingsMenuOpen(false);
  };

  const handleSettingsToggle = (event: Event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsSettingsMenuOpen((current) => !current);
  };

  const settingsItems: Array<{
    key: Exclude<SettingsModalKey, null>;
    icon: LucideIcon;
    label: string;
    trailingLabel?: string;
  }> = [
    { key: "profile", icon: UserPen, label: t("common.editProfile") },
    { key: "loginInfo", icon: Lock, label: t("common.loginInfo") },
    {
      key: "language",
      icon: Globe,
      label: t("common.language"),
      trailingLabel: userInfo?.account?.language
        ? getLanguageLabel(userInfo.account.language)
        : undefined,
    },
    { key: "notification", icon: Bell, label: t("common.notification") },
    { key: "blockedUsers", icon: UserX, label: t("common.blockedUsersOnMenu") },
    { key: "usage", icon: BarChart3, label: t("common.usage") },
  ];

  return (
    <>
      <nav className="fixed inset-x-0 top-0 z-50 h-[60px] bg-rw-sub">
        <div className="flex h-full flex-1 items-center justify-center px-4">
          {/* Left section */}
          <div className="flex items-center gap-4">
            <Link
              to="/"
              className="flex items-center gap-2 transition-opacity hover:opacity-80"
              aria-label="Go to home page"
            >
              <img src="/logo-white.png" alt="logo" className="h-8" />
            </Link>
          </div>

          {/* Spacer to push right content to the right */}
          <div className="flex-1" />

          {/* Right section */}
          <div className="flex items-center justify-center gap-3">
            <AppToggle />

            <MenuButton
              IconComponent={Calendar}
              onClick={() => navigate({ to: "/calendar" })}
            />

            <DropdownMenu
              open={isMenuOpen}
              onOpenChange={(open) => {
                setIsMenuOpen(open);
                if (!open) {
                  setIsSettingsMenuOpen(false);
                }
              }}
            >
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="size-9 rounded-full bg-white p-0"
                >
                  {userInfo?.display_name || userInfo?.image_url ? (
                    <CustomAvatar
                      name={userInfo?.display_name ?? t("common.profile")}
                      imageUrl={userInfo?.image_url}
                      size={36}
                      roundedFull
                    />
                  ) : (
                    <User />
                  )}
                </Button>
              </DropdownMenuTrigger>

              <DropdownMenuContent align="end" className="w-[200px]">
                <DropdownMenuLabel>
                  <span className="block truncate text-gray-500">
                    {userInfo?.display_name}
                  </span>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={navigateToUserProfile}>
                  <User className="size-4" />
                  {t("common.viewProfile")}
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={handleSettingsToggle}>
                  <Settings className="size-4" />
                  {t("common.basicSettings")}
                  <ChevronDown
                    className={`ml-auto size-4 transition-transform ${
                      isSettingsMenuOpen ? "rotate-180" : ""
                    }`}
                  />
                </DropdownMenuItem>
                {isSettingsMenuOpen &&
                  settingsItems.map((item) => (
                    <DropdownMenuItem
                      key={item.key}
                      inset
                      className="text-xs"
                      onClick={() => openModal(item.key)}
                    >
                      <item.icon className="size-4 shrink-0" />
                      <span className="min-w-0 flex-1 truncate">
                        {item.label}
                      </span>
                      {item.trailingLabel ? (
                        <span className="ml-auto max-w-[72px] truncate text-[11px] text-muted-foreground">
                          {item.trailingLabel}
                        </span>
                      ) : null}
                    </DropdownMenuItem>
                  ))}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout}>
                  <LogOut className="size-4" />
                  {t("common.logout")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </nav>

      {/* Modals */}
      <ProfileEditModal
        isOpen={activeModal === "profile"}
        onClose={() => setActiveModal(null)}
      />
      <LanguageModal
        isOpen={activeModal === "language"}
        onClose={() => setActiveModal(null)}
      />
      <LoginInfoModal
        isOpen={activeModal === "loginInfo"}
        onClose={() => setActiveModal(null)}
        onLogout={logout}
      />
      <EmailNotificationModal
        isOpen={activeModal === "notification"}
        onClose={() => setActiveModal(null)}
      />
      <BlockedUsersModal
        isOpen={activeModal === "blockedUsers"}
        onClose={() => setActiveModal(null)}
      />
      <UsageModal
        isOpen={activeModal === "usage"}
        onClose={() => setActiveModal(null)}
      />
    </>
  );
};
